import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Eye, Radio, ShieldAlert } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import { EmptyRow, ErpTable, Panel, Td, Th } from '@/components/erp/primitives'
import { can as hasPermission, useSession } from '@/shared/auth/session'
import {
  getDossier, getSubject, listCategories, listPlans, runPlanNow, setPublicFigure,
  type CategoryBadge, type Dossier, type Item, type SubjectDetail,
} from './api'
import {
  ClearanceBadge, CollectedAt, GradeChip, InfoRow, InitialsAvatar, Pill, ScoreRing, SubHeader,
  TabBar, fmtDate, label,
} from './components/primitives'
import { ItemDetail } from './components/ItemDetail'

/**
 * The Subject File — a port of PollSphere's candidate profile sheet.
 *
 * ## It is a route, not an overlay
 *
 * PollSphere renders this as a full-screen sheet. Here it has a URL, because a dossier is a
 * thing somebody sends to a colleague, and a modal that cannot be linked to gets screenshotted
 * instead — which is the same file, out of the platform, with no audit trail.
 *
 * ## The tab strip comes from the server
 *
 * `GET /subjects/{id}/categories` returns the tabs with their counts and an `available` flag.
 * Hard-coding twelve tabs here would mean a new category needs a frontend release, and an
 * item could land in a tab nothing renders. A tab the caller may not read is DISABLED rather
 * than hidden: a vanished tab reads as "there is nothing here", which is a different fact
 * from "you are not cleared for this".
 *
 * ## Every row carries its grade and its custody date
 *
 * The `GradeChip` and `CollectedAt` on every item row are the point of the screen. Without
 * them a `D5` tweet and an `A1` judgment look identical, and a reader briefs from both in the
 * same voice.
 */
/**
 * Which collector fills which tab.
 *
 * Here rather than on the server because it answers a UI question — "what would this button
 * run" — and the server's own category map is about where an ITEM lands, which is a different
 * relation: several collectors can fill one tab, and one collector can fill several.
 */
const COLLECTORS_BY_CATEGORY: Record<string, string[]> = {
  SOCIAL_INTEL: ['social.aggregator'],
  MEDIA_COVERAGE: ['news.rss'],
  PUBLIC_STATEMENTS: ['news.rss'],
}

export function SubjectFilePage() {
  const { id = '' } = useParams()
  const [detail, setDetail] = useState<SubjectDetail | null>(null)
  const [categories, setCategories] = useState<CategoryBadge[]>([])
  const [active, setActive] = useState<string | null>(null)
  const [dossier, setDossier] = useState<Dossier | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ruling, setRuling] = useState(false)
  const [collecting, setCollecting] = useState(false)
  const [collectNote, setCollectNote] = useState<string | null>(null)
  // The table is a summary; this is the record. See ItemDetail's own comment.
  const [openItem, setOpenItem] = useState<Item | null>(null)
  const authorities = useSession((st) => st.authorities)

  const loadHeader = useCallback(async () => {
    try {
      setError(null)
      const [d, c] = await Promise.all([getSubject(id), listCategories(id)])
      setDetail(d)
      setCategories(c)
      setActive((current) => current ?? c.find((cat) => cat.itemCount > 0)?.code ?? c[0]?.code ?? null)
    } catch (err) {
      setError(explain(err))
    }
  }, [id])

  const loadTab = useCallback(async () => {
    if (!active) return
    try {
      setDossier(await getDossier(id, { category: active }))
    } catch (err) {
      setError(explain(err))
    }
  }, [id, active])

  useEffect(() => { void loadHeader() }, [loadHeader])
  useEffect(() => { void loadTab() }, [loadTab])

  /**
   * Collect this tab's material now, rather than waiting for the plan's cadence.
   *
   * A dossier is read when somebody is ABOUT to need it — before a debate, after an
   * allegation — and "come back in six hours" is not an answer at that moment. The scheduled
   * run still exists and is still the norm; this is the same run, asked for by a person.
   *
   * It runs every enabled plan for the tab's collector, narrowed to THIS subject. Narrowing
   * matters: a plan may cover a hundred subjects, and a button on one person's file that
   * silently collected on all of them would be a surprise the operator pays for.
   */
  const collectNow = useCallback(async () => {
    if (!active) return
    setCollecting(true)
    setCollectNote(null)
    try {
      const plans = await listPlans()
      const relevant = plans.filter(
        (p) => p.enabled
          && COLLECTORS_BY_CATEGORY[active]?.includes(p.collectorCode)
          && (p.subjectId == null || p.subjectId === id),
      )
      if (relevant.length === 0) {
        // Names the missing thing. "Nothing happened" would send them to the wrong screen.
        setCollectNote('No enabled collection plan covers this tab. Add one under '
          + 'Dossier \u2192 Plans, then collect again.')
        return
      }
      const runs = await Promise.all(relevant.map((p) => runPlanNow(p.id, id)))
      const found = runs.reduce((n, r) => n + (r.itemsNew ?? 0), 0)
      const only = runs.length === 1 ? runs[0] : null
      setCollectNote(only
        ? `Run ${only.statusCode.toLowerCase()} \u2014 ${found} new item(s).`
        : `${runs.length} plans run \u2014 ${found} new item(s).`)
      await loadHeader()
      await loadTab()
    } catch (err) {
      setCollectNote(explain(err))
    } finally {
      setCollecting(false)
    }
  }, [active, id, loadHeader, loadTab])

  if (error && !detail) return <ErrorStrip message={error} />
  if (!detail) return <LoadingBlock label="Opening the file" />

  const s = detail.subject

  return (
    <div className="space-y-3">
      <Panel>
        <div className="flex flex-wrap items-start gap-3 p-3">
          <InitialsAvatar name={s.displayName} size={40} />
          <div className="min-w-48 flex-1 space-y-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <h2 className="text-[15px] font-semibold">{s.displayName}</h2>
              <Pill className="border-border">{label(s.subjectTypeCode)}</Pill>
              <ClearanceBadge clearance={s.sensitivityCode === 'SENSITIVE' ? 'RESTRICTED' : null} />
              {s.publicFigure && (
                <Pill className="border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400">
                  {label(s.publicFigureBasisCode)}
                </Pill>
              )}
              {s.watchlist && <Eye className="size-3.5 text-primary" aria-label="Watched" />}
              {s.collectionEnabled && (
                <span className="inline-flex items-center gap-1 text-[11px] text-rose-600"
                      title={s.collectionPurpose ?? undefined}>
                  <Radio className="size-3" />Collecting
                </span>
              )}
            </div>
            {/* TWO dates, never one: when we last learned something, and when the file was
                opened. A single "last updated" cannot answer either question properly. */}
            <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
              <span>Last collected: <CollectedAt at={s.lastActivityAt} /></span>
              <span>File opened {fmtDate(s.firstSeenAt)}</span>
              <span>{s.itemCount} item{s.itemCount === 1 ? '' : 's'}</span>
              <span>Legal basis: {label(s.legalBasisCode)}</span>
            </div>
            {s.slug && (
              <p className="font-mono text-[10px] text-muted-foreground">{s.slug}</p>
            )}
          </div>

          <div className="flex gap-3">
            {(dossier?.assessments ?? []).slice(0, 3).map((a) => (
              <ScoreRing key={a.id} value={a.score} label={label(a.kindCode)}
                         tone={a.kindCode === 'RISK' || a.kindCode === 'THREAT'
                           ? 'danger' : 'primary'} />
            ))}
          </div>
        </div>

        {hasPermission(authorities, 'intel:admin') && (
          <PublicFigureRuling
            subject={s}
            open={ruling}
            onOpen={setRuling}
            onSaved={() => { setRuling(false); void loadHeader() }}
            onError={setError}
          />
        )}

        {detail.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 border-t border-border px-3 py-1.5">
            {detail.tags.map((t) => <Pill key={t} className="border-border">{t}</Pill>)}
          </div>
        )}
      </Panel>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
      <TabBar
        tabs={categories.map((c) => ({
          code: c.code, label: c.label, count: c.itemCount, available: c.available,
          hint: c.lastCollectedAt ? `Last collected ${fmtDate(c.lastCollectedAt)}` : undefined,
        }))}
        active={active}
        onSelect={setActive}
      />
        </div>
        {active && COLLECTORS_BY_CATEGORY[active] && (
          <button type="button" onClick={() => void collectNow()} disabled={collecting}
                  className="shrink-0 rounded border border-border px-2 py-1 text-[11px]
                             hover:bg-muted/40 disabled:opacity-50">
            {collecting ? 'Collecting\u2026' : 'Collect now'}
          </button>
        )}
      </div>

      {collectNote && (
        <p className="rounded border border-border bg-muted/30 px-3 py-2 text-[11px]
                      text-muted-foreground">
          {collectNote}
        </p>
      )}

      {error && <ErrorStrip message={error} />}
      {!dossier && !error && <LoadingBlock label="Loading tab" />}

      <ItemDetail
        item={openItem}
        open={openItem != null}
        onClose={() => setOpenItem(null)}
        canAttach={hasPermission(authorities, 'intel:ingest')}
        canMark={hasPermission(authorities, 'intel:triage')}
      />

      {dossier && (
        <div className="space-y-3">
          {dossier.emptyMessage && (
            // Names what WOULD fill the tab. An empty panel reads as "there is nothing";
            // this reads as "nobody has measured this", and they are different facts.
            <p className="rounded border border-dashed border-border px-3 py-4 text-[12px]
                          text-muted-foreground">
              {dossier.emptyMessage}
            </p>
          )}

          {dossier.items.withheldSensitive > 0 && (
            <p className="flex items-center gap-1.5 rounded border border-amber-500/30
                          bg-amber-500/5 px-2 py-1 text-[11px] text-amber-700 dark:text-amber-400">
              <ShieldAlert className="size-3.5" />
              {dossier.items.withheldSensitive} item(s) withheld — your authorities do not
              include intel:read-sensitive.
            </p>
          )}

          {dossier.items.content.length > 0 && (
            <Panel>
              <ErpTable head={<>
                <Th>Grade</Th>
                <Th>Item</Th>
                <Th className="axis-hide-sm">Type</Th>
                <Th>Veracity</Th>
                <Th className="axis-hide-sm">Occurred</Th>
                <Th>Collected</Th>
              </>}>
                {dossier.items.content.map((item) => (
                  <tr key={item.id} tabIndex={0} role="button"
                      onClick={() => setOpenItem(item)}
                      onKeyDown={(e) => {
                        // The row IS the way into the record, so it has to answer a keyboard.
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setOpenItem(item)
                        }
                      }}
                      className="cursor-pointer hover:bg-muted/40
                                 focus:bg-muted/60 focus:outline-none">
                    <Td><GradeChip grade={item.grading} /></Td>
                    <Td>
                      {item.redacted ? (
                        <span className="italic text-muted-foreground"
                              title={item.redactedReason ?? undefined}>
                          [redacted — {item.redactedReason}]
                        </span>
                      ) : (
                        <>
                          <span>{item.title ?? '(untitled)'}</span>
                          {item.url && (
                            <a href={item.url} target="_blank" rel="noreferrer"
                               className="ml-1 text-[10px] text-primary underline">source</a>
                          )}
                        </>
                      )}
                    </Td>
                    <Td className="axis-hide-sm">
                      <span className="text-[11px] text-muted-foreground">
                        {label(item.itemTypeCode)}
                      </span>
                    </Td>
                    <Td>
                      <Pill className={item.veracityCode === 'DEBUNKED'
                        || item.veracityCode === 'DISPUTED'
                        ? 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-400'
                        : 'border-border'}>
                        {label(item.veracityCode)}
                      </Pill>
                    </Td>
                    <Td className="axis-hide-sm">
                      {/* Null occurred_at sorts last and prints as unknown — never guessed. */}
                      <span className="text-[11px] text-muted-foreground">
                        {item.occurredAt ? fmtDate(item.occurredAt) : 'date unknown'}
                      </span>
                    </Td>
                    <Td><CollectedAt at={item.collectedAt} /></Td>
                  </tr>
                ))}
              </ErpTable>
            </Panel>
          )}

          {dossier.attributes.length > 0 && (
            <Panel>
              <div className="p-2">
                <SubHeader>What is recorded</SubHeader>
                {dossier.attributes.map((a) => (
                  <InfoRow key={a.id} label={a.label}>
                    <span>{a.valueText ?? a.valueNumber ?? a.valueDate
                      ?? String(a.valueBool ?? '—')}</span>
                    <span className="ml-2 text-[10px] text-muted-foreground">
                      as of {fmtDate(a.asOf)} · {label(a.methodCode)}
                    </span>
                  </InfoRow>
                ))}
              </div>
            </Panel>
          )}

          {dossier.assessments.length > 0 && (
            <Panel>
              <div className="space-y-2 p-2">
                <SubHeader>Judgements on record</SubHeader>
                {dossier.assessments.map((a) => (
                  <div key={a.id} className="rounded border border-border p-2">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="text-[12px] font-medium">{label(a.kindCode)}</span>
                      <span className="text-[12px] tabular-nums">
                        {a.score == null ? 'not scored' : Math.round(a.score)}
                        {a.band && <span className="ml-1 text-muted-foreground">{a.band}</span>}
                      </span>
                    </div>
                    {/* The rationale is shown, always. A score without its reason is a number
                        somebody will quote as a fact. */}
                    <p className="mt-1 text-[11px] text-muted-foreground">{a.rationale}</p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {label(a.methodCode)} · {fmtDate(a.assessedAt)}
                    </p>
                  </div>
                ))}
              </div>
            </Panel>
          )}

          {dossier.relationships.length > 0 && (
            <Panel>
              <ErpTable head={<>
                <Th>Relationship</Th>
                <Th>Counterpart</Th>
                <Th className="axis-hide-sm">Since</Th>
                <Th className="text-right">Confidence</Th>
              </>}>
                {dossier.relationships.map((r) => (
                  <tr key={r.id}>
                    <Td>{label(r.relationshipTypeCode)}</Td>
                    <Td>{r.roleLabel ?? '—'}</Td>
                    <Td className="axis-hide-sm">{fmtDate(r.since)}</Td>
                    <Td className="text-right tabular-nums">{r.confidence}</Td>
                  </tr>
                ))}
              </ErpTable>
            </Panel>
          )}

          {dossier.items.content.length === 0 && dossier.attributes.length === 0
            && !dossier.emptyMessage && (
            <Panel>
              <ErpTable head={<Th>Nothing here</Th>}>
                <EmptyRow colSpan={1} message="Nothing collected in this category yet." />
              </ErpTable>
            </Panel>
          )}
        </div>
      )}
    </div>
  )
}

const BASES = ['ELECTED_OFFICE', 'DECLARED_CANDIDACY', 'PARTY_OFFICIAL', 'PUBLIC_APPOINTEE',
  'STATE_OFFICER', 'SELF_PUBLISHED']

/**
 * The public-figure ruling — the gate on everything Opposition Command can do.
 *
 * A target may only be opened on a subject recorded as a public figure, with a BASIS and
 * somebody's name on the ruling. That is the one refusal that defines the opposition feature,
 * so the ruling needs a place a human can make it; without this control the rule was
 * enforceable but unreachable, and the only way to mark a rival was the API.
 *
 * Withdrawing is offered in the same control and is not a delete: the basis reverts to
 * NOT_A_PUBLIC_FIGURE and the change is in the audit trail with both values.
 */
function PublicFigureRuling({ subject, open, onOpen, onSaved, onError }: {
  subject: SubjectDetail['subject']
  open: boolean
  onOpen: (v: boolean) => void
  onSaved: () => void
  onError: (message: string) => void
}) {
  const [basisCode, setBasisCode] = useState('DECLARED_CANDIDACY')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  async function save(on: boolean) {
    setBusy(true)
    try {
      await setPublicFigure(subject.id, on, on ? basisCode : undefined, note.trim() || undefined)
      setNote('')
      onSaved()
    } catch (err) {
      onError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-t border-border px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] text-muted-foreground">
          {subject.publicFigure
            ? `Public figure — ${label(subject.publicFigureBasisCode)}`
            : 'Not recorded as a public figure. No opposition target can be opened on them.'}
        </span>
        <button type="button" onClick={() => onOpen(!open)}
                className="rounded border border-border px-2 py-0.5 text-[11px]">
          {open ? 'Close' : subject.publicFigure ? 'Change the ruling' : 'Record the ruling'}
        </button>
        {subject.publicFigureNote && !open && (
          <span className="text-[11px] italic text-muted-foreground">
            {subject.publicFigureNote}
          </span>
        )}
      </div>

      {open && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Basis
            </span>
            <select value={basisCode} onChange={(e) => setBasisCode(e.target.value)}
                    className="h-7 rounded border border-input bg-background px-2 text-[12px]">
              {BASES.map((b) => <option key={b} value={b}>{label(b)}</option>)}
            </select>
          </label>
          <label className="flex flex-1 flex-col gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              How you know (optional, kept on the ruling)
            </span>
            <input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="Gazetted as the sitting MCA for this ward."
                   className="h-7 rounded border border-input bg-background px-2 text-[12px]" />
          </label>
          <button type="button" disabled={busy} onClick={() => void save(true)}
                  className="rounded bg-primary px-3 py-1.5 text-[12px] font-medium
                             text-primary-foreground disabled:opacity-50">
            {busy ? 'Recording…' : 'Record'}
          </button>
          {subject.publicFigure && (
            <button type="button" disabled={busy} onClick={() => void save(false)}
                    className="rounded border border-rose-500/40 px-3 py-1.5 text-[12px]
                               text-rose-700 disabled:opacity-50 dark:text-rose-400">
              Withdraw
            </button>
          )}
        </div>
      )}
    </div>
  )
}
